/**
 * Formato do documento gravado com a conversa — peças puras (sem Office.js).
 *
 * Existem DOIS formatos no banco, e o painel precisa ler os dois:
 *
 *  - **.docx inteiro** (atual): `docx:base64,<arquivo>`. É o arquivo como o
 *    Word o salvaria — estilos, tema, seções (margens, "primeira página
 *    diferente"), cabeçalho e rodapé. Volta pelo mesmo caminho do template
 *    (`insertFileFromBase64`), que é o único que traz tudo isso de volta.
 *  - **OOXML do corpo** (legado): o `body.getOoxml()` cru, que começa com `<`.
 *    Reinserido com `insertOoxml`, os estilos de MESMO NOME do documento de
 *    destino vencem os do pacote — num documento em branco, Normal vira
 *    Calibri preto e Título 1 vira Calibri Light azul — e a seção perde
 *    "primeira página diferente", então a capa ganha o cabeçalho das páginas
 *    internas. Foi o "documento todo desformatado" visto ao retomar conversa.
 *
 * O .docx pode levar METADADOS no cabeçalho, antes da vírgula:
 * `docx:base64;fp=<impressão digital>;at=<ISO>,<arquivo>`. A impressão digital
 * do texto e a hora do salvamento descrevem exatamente AQUELE arquivo — a
 * retomada compara com o documento aberto para saber se ele mudou desde então.
 * Viajam juntos na mesma coluna (`document_ooxml`) de propósito: mudar o schema
 * pediria DDL no RDS compartilhado, que o usuário da aplicação não tem, e
 * colunas à parte poderiam descrever um documento que já não é o gravado.
 * Base64 nunca contém vírgula, então o cabeçalho é tudo até a primeira.
 */
export const DOCX_SNAPSHOT_PREFIX = 'docx:base64,';
const DOCX_HEADER = 'docx:base64';
/** O valor gravado é um .docx inteiro (e não o OOXML legado do corpo)? */
export function isDocxSnapshot(snapshot) {
    if (typeof snapshot !== 'string' || !snapshot.startsWith(DOCX_HEADER))
        return false;
    const depois = snapshot.charAt(DOCX_HEADER.length);
    return depois === ',' || depois === ';';
}
/** Base64 do .docx, sem o cabeçalho (com ou sem metadados). */
export function docxSnapshotBase64(snapshot) {
    return snapshot.slice(snapshot.indexOf(',') + 1);
}
/** Metadados do cabeçalho. Formato antigo ou legado: objeto vazio. */
export function snapshotMeta(snapshot) {
    if (!isDocxSnapshot(snapshot))
        return {};
    const cabecalho = snapshot.slice(0, snapshot.indexOf(','));
    const meta = {};
    for (const parte of cabecalho.split(';').slice(1)) {
        const igual = parte.indexOf('=');
        if (igual <= 0)
            continue;
        const chave = parte.slice(0, igual);
        const valor = parte.slice(igual + 1);
        if (chave === 'fp' && valor)
            meta.fingerprint = valor;
        if (chave === 'at' && valor)
            meta.savedAt = valor;
    }
    return meta;
}
/** Um valor de metadado não pode carregar os separadores do cabeçalho. */
const limparValor = (v) => v.replace(/[,;=\s]/g, '');
/**
 * Monta o valor gravado a partir das fatias de `getFileAsync` (cada uma um
 * array de bytes). Codifica em blocos: `String.fromCharCode(...bytes)` de um
 * arquivo de vários MB estoura a pilha de argumentos.
 */
export function encodeDocxSnapshot(slices, meta = {}) {
    const BLOCO = 0x8000;
    let binario = '';
    for (const fatia of slices) {
        const bytes = fatia instanceof Uint8Array ? fatia : Uint8Array.from(fatia);
        for (let i = 0; i < bytes.length; i += BLOCO) {
            binario += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + BLOCO)));
        }
    }
    let cabecalho = DOCX_HEADER;
    if (meta.fingerprint)
        cabecalho += `;fp=${limparValor(meta.fingerprint)}`;
    if (meta.savedAt)
        cabecalho += `;at=${limparValor(meta.savedAt)}`;
    return `${cabecalho},${btoa(binario)}`;
}
// ---------------------------------------------------------------------------
// Impressão digital do texto
// ---------------------------------------------------------------------------
/** FNV-1a de 32 bits, com semente — duas sementes dão 64 bits de hash. */
function fnv1a(texto, semente) {
    let h = semente >>> 0;
    for (let i = 0; i < texto.length; i++) {
        h ^= texto.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}
/**
 * Impressão digital do TEXTO de um documento: `v1-<tamanho>-<hash>`.
 *
 * Serve para uma pergunta só: "o documento aberto é o mesmo que foi gravado?".
 * Espaço em branco é normalizado — um parágrafo vazio a mais no fim, ou `\r`
 * contra `\n`, não é mudança que o usuário reconheça. Formatação não entra (o
 * `body.text` não a enxerga): se só ela mudou, a retomada mantém o documento
 * aberto, que é justamente o que o usuário tem na tela. Não é criptografia —
 * é um hash de 64 bits para comparar, e o texto nunca sai daqui.
 */
export function fingerprintText(texto) {
    const normal = (texto || '').replace(/\s+/g, ' ').trim();
    const a = fnv1a(normal, 0x811c9dc5).toString(16).padStart(8, '0');
    const b = fnv1a(normal, 0x01000193).toString(16).padStart(8, '0');
    return `v1-${normal.length}-${a}${b}`;
}
